import * as React from 'react';
import { useSelector } from 'react-redux';
import { ArticleHeading3, ArticleParagraph2, LinkButton } from '@cian/ui-kit';
import { NewBuildingCard as NewBuildingCardComponent, NewBuildingCardListed } from '../../components/NewBuildingCard';
import { Link } from '../../components/Link';
import { IApplicationState } from '../../types/redux';
import {
  checkFsk,
  getDeveloperId,
  getFilterRegionId,
  getNewBuildingById,
  getNewBuildingPromos,
  getNewbuildingsJsonQuery,
} from '../../selectors';
import { TViewMode } from '../Projects/types';
import { CardImage } from '../CardImage';
import { PromoBlockContainer } from '../PromoBlock';
import { NewBuildingLabels } from '../NewBuildingLabels';
import { SubwayInfo } from '../SubwayInfo';
import { ApartmentsList, ApartmentsListGroup } from '../ApartmentsList';
import { getNewBuildingImages } from './helpers';
import {
  trackApartmentsCardClick,
  trackImageCardClick,
  trackMoreButtonCardClick,
  trackSeeAllButtonCardClick,
  trackTitleCardClick,
} from './tracking';
import { SPThemeWrapper } from '../../components/SPThemeWrapper';

interface INewBuildingCardProps {
  newbuildingId: number;
  viewMode: TViewMode;
  index: number;
}

export function NewBuildingCard({ newbuildingId, viewMode, index }: INewBuildingCardProps) {
  const developerId = useSelector(getDeveloperId);
  const isFsk = useSelector(checkFsk);
  const newbuilding = useSelector((state: IApplicationState) => getNewBuildingById(state, newbuildingId));
  const sc = useSelector(getNewbuildingsJsonQuery);
  const promos = useSelector((state: IApplicationState) => getNewBuildingPromos(state, newbuildingId));
  const regionId = useSelector(getFilterRegionId);
  const images = getNewBuildingImages(newbuilding);
  const title = <ArticleHeading3>{newbuilding.title}</ArticleHeading3>;
  const finishLabel = newbuilding.finishLabel ? <ArticleParagraph2>{newbuilding.finishLabel}</ArticleParagraph2> : null;
  const address = <ArticleParagraph2 color="gray60_100">{newbuilding.address}</ArticleParagraph2>;
  const labels = <NewBuildingLabels newBuildingId={newbuildingId} />;
  const url = newbuilding.publicUrl || '';
  const subwayInfo = <SubwayInfo subwayInfo={newbuilding.subwayInfo} />;
  const promo = <PromoBlockContainer promos={promos} url={url} />;
  const trackingArguments = React.useMemo(
    () => ({ newbuilding, developerId, position: index + 1, regionId, sc: sc as Object }),
    [developerId, index, newbuilding, regionId, sc],
  );

  return viewMode === 'table' ? (
    <NewBuildingCardComponent
      isReliable={newbuilding.isReliable}
      promo={promo}
      image={<CardImage size="M" src={newbuilding.image} borderRadius={0} />}
      title={title}
      url={url}
      labels={labels}
      finishLabel={finishLabel}
      subwayInfo={subwayInfo}
      address={address}
      apartments={<ApartmentsList newBuildingId={newbuilding.id} />}
      onTitleClick={trackTitleCardClick(trackingArguments)}
      onImageClick={trackImageCardClick(trackingArguments)}
      onApartmentsClick={trackApartmentsCardClick(trackingArguments)}
    />
  ) : (
    <NewBuildingCardListed
      isReliable={newbuilding.isReliable}
      images={images}
      promo={promo}
      title={title}
      labels={labels}
      finishLabel={finishLabel}
      subwayInfo={subwayInfo}
      address={address}
      apartments={<ApartmentsListGroup newBuildingId={newbuilding.id} />}
      detailsButton={
        <SPThemeWrapper theme={isFsk ? 'fsk_fill_primary' : undefined}>
          <LinkButton
            size="XS"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackMoreButtonCardClick(trackingArguments)}
          >
            Подробнее о ЖК
          </LinkButton>
        </SPThemeWrapper>
      }
      seeAll={
        newbuilding.searchList ? (
          <Link
            url={newbuilding.searchList}
            text="Смотреть все квартиры"
            onClick={trackSeeAllButtonCardClick(trackingArguments)}
          />
        ) : null
      }
    />
  );
}
